import React from "react";
import { Link } from "react-router-dom";
import { Radio, Eye, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { usePageMeta, useLive } from "@/lib/site";
import { useAuth } from "@/context/AuthContext";
import { liveApi } from "@/lib/stageExtraApi";
import { CreatorAvatar, VerifiedMark } from "@/components/stage/PostCard";

function StreamCard({ s, live }) {
  const c = s.creator || {};
  const body = (
    <>
      <div className="relative aspect-video overflow-hidden rounded-2xl bg-muted">
        {s.cover_url && <img src={s.cover_url} alt="" loading="lazy" className="h-full w-full object-cover" />}
        {live ? (
          <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-rose-600 px-2.5 py-1 text-[11px] font-bold text-white"><Radio size={11} /> LIVE</span>
        ) : (
          <span className="absolute left-3 top-3 rounded-full bg-background/90 px-2.5 py-1 text-[11px] font-semibold">Ended</span>
        )}
        <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-medium text-white"><Eye size={11} /> {live ? s.viewers : s.peak_viewers}</span>
        {s.ticket && <span className="absolute bottom-3 left-3 rounded-full bg-background/90 px-2 py-0.5 text-[11px] font-semibold">Ticket £{Number(s.ticket.price_gbp).toFixed(2)}</span>}
      </div>
      <div className="mt-2 flex items-start gap-2.5">
        <CreatorAvatar author={c} size={36} />
        <div className="min-w-0">
          <p className="truncate font-semibold">{s.title || "Live"}</p>
          <p className="flex items-center gap-1 truncate text-xs text-muted-foreground">{c.display_name || c.handle} <VerifiedMark author={c} size={12} />{s.category_label ? ` · ${s.category_label}` : ""}</p>
        </div>
      </div>
    </>
  );
  return c.handle ? <Link to={`/@${c.handle}`} className="block" data-testid={`stream-${s.id}`}>{body}</Link> : <div data-testid={`stream-${s.id}`}>{body}</div>;
}

export default function Live() {
  usePageMeta("Live", "Who's live right now on Fable + Stage.");
  const { user } = useAuth();
  const data = useLive(() => liveApi.now(), [user?.id]);
  const following = useLive(() => (user ? liveApi.following() : Promise.resolve({ items: [] })), [user?.id]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-serif-display text-3xl font-bold sm:text-4xl">Live</h1>
          <p className="mt-1 text-muted-foreground">Watch creators live. Going live, chat and gifts are in the Fable + app.</p>
        </div>
        <Link to="/get-the-app"><Button variant="outline" className="rounded-full"><Smartphone size={15} className="mr-1.5" /> Go live in the app</Button></Link>
      </div>

      {data === undefined ? (
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="aspect-video rounded-2xl" />)}</div>
      ) : data === null ? (
        <p className="mt-10 text-center text-muted-foreground">Couldn't load Live right now.</p>
      ) : (
        <>
          {following?.items?.length > 0 && (
            <section className="mt-8">
              <h2 className="text-lg font-bold">People you follow</h2>
              <div className="mt-3 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{following.items.map((s) => <StreamCard key={s.id} s={s} live />)}</div>
            </section>
          )}
          {data.items.length > 0 ? (
            <section className="mt-8" data-testid="live-now">
              <h2 className="text-lg font-bold">Live now</h2>
              <div className="mt-3 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{data.items.map((s) => <StreamCard key={s.id} s={s} live />)}</div>
            </section>
          ) : (
            <div className="mt-8 rounded-2xl border border-dashed border-border p-10 text-center" data-testid="live-empty">
              <Radio size={24} className="mx-auto text-muted-foreground" />
              <p className="mt-2 font-semibold">No one's live right now</p>
              <p className="mt-1 text-sm text-muted-foreground">Be the first to go live — your followers get a nudge the moment you start.</p>
            </div>
          )}
          {data.recent?.length > 0 && (
            <section className="mt-10">
              <h2 className="text-lg font-bold">Recently live</h2>
              <div className="mt-3 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{data.recent.map((s) => <StreamCard key={s.id} s={s} />)}</div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
